// Copyright ©️ 2026 eVolve MEP, LLC

import { type UpdateWorkOrderItemBody, useEvolveGet, type WorkOrder, type WorkOrderItem } from '@evolvemep/api-ts';
import { ActionIcon, ColorSwatch, Divider, Flex, Loader, Space, Stack, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AttachmentList } from 'components/Mantine/Attachments/AttachmentList';
import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { isNotNil } from 'helpers/isNotNil';
import { useManufacturers } from 'hooks/typeCacheHooks/useManufacturers';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { FabricationItems } from 'modules/Shop/EvolveFabrication/FabricationItems';
import {
  getDisplayNameWO,
  getTaskStatusFromItem,
  hasEverBeenReleased,
} from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { EditItemMenu } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/EditItemMenu';
import { EditQtyMfg } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/EditQtyMfg';
import { WorkOrderItemBOM } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemBOM';
import { WorkOrderItemTaskList } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/WorkOrderItemTaskList';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

export type WorkOrderItemModalProps = ModalRegistryManagedProps & {
  workOrder: WorkOrder;
  workOrderItem: WorkOrderItem;
  onDeleteWorkOrderItem?: (workOrderItem: WorkOrderItem) => void;
  updateWorkOrderItem: (body: UpdateWorkOrderItemBody) => Promise<void>;
  navProps?: {
    onNext: () => void;
    onPrev: () => void;
    hasNext: boolean;
    hasPrev: boolean;
  };
  refreshWorkOrderItem: () => Promise<void>;
  refreshing: boolean;
  onItemsAdded?: () => void;
  selectedModalTab?: WorkOrderItemModalTab;
  onModalTabChange?: (tab: WorkOrderItemModalTab) => void;
  onTaskAttachmentsChanged?: () => void;
};

export type WorkOrderItemModalTab = 'tasks' | 'bom' | 'fabrication-items' | 'shipments' | 'attachments';

const WorkOrderItemShipmentsTab = ({ workOrderItem }: { workOrderItem: WorkOrderItem }) => {
  const { data: shipments = [], loading: loadingShipments } = useEvolveGet(
    `shop/workOrder/item/${workOrderItem.workOrderItemId}/shipments`,
    { lazy: false },
  );

  if (loadingShipments) {
    return <Loader size="sm" />;
  }

  if (shipments.length === 0) {
    return (
      <Text fz="sm" c="dimmed">
        This item is not allocated to any shipping orders.
      </Text>
    );
  }

  return (
    <Stack gap="xs">
      {shipments.map((shipment) => (
        <Flex key={shipment.shipmentItemId} align="center" justify="space-between" gap="md">
          <Stack gap={0} flex={1}>
            <EvolveLink to={`/shipping-orders/${shipment.shipmentId}`} from="Work Orders">
              {shipment.shipmentIdentifier}: {shipment.shipmentName}
            </EvolveLink>
            <Text fz="xs" c="dimmed">
              {shipment.allocatedQuantity} allocated
            </Text>
          </Stack>
          <EntityStatusBadge
            customStatusEntityName="Shipment"
            stateName={shipment.shipmentStateName}
            statusId={shipment.shipmentStatusId}
          />
        </Flex>
      ))}
      <Divider />
      <Text fz="sm" fw={600}>
        Total allocated: {shipments.reduce((total, shipment) => total + shipment.allocatedQuantity, 0)}
      </Text>
    </Stack>
  );
};

export const WorkOrderItemModal = ({
  workOrder,
  workOrderItem,
  updateWorkOrderItem,
  onDeleteWorkOrderItem,
  navProps,
  refreshWorkOrderItem,
  refreshing,
  onItemsAdded,
  selectedModalTab,
  onModalTabChange,
  onTaskAttachmentsChanged,
  ...props
}: WorkOrderItemModalProps) => {
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const hasTasksAccess = useHasAccess('tasks', 'write');
  const hasFabricationAccess = useHasAccess('fabrication', 'read');

  const { hasNext, hasPrev, onNext, onPrev } = navProps ?? {};
  const { manufacturers } = useManufacturers();

  const editable = canEditWorkOrder('editItems');

  const mfgName = manufacturers?.find((m) => m.manufacturerId === workOrderItem.manufacturerId)?.name ?? '';

  return (
    <WrappedModal
      title={workOrderItem.name}
      subtitle={
        <Text title={`Manufacturer: ${mfgName} | Catalog #: ${workOrderItem.manufacturerCatalogNumber || 'N/A'}`}>
          {mfgName}
          {!!workOrderItem.manufacturerCatalogNumber && ` | ${workOrderItem.manufacturerCatalogNumber}`}
        </Text>
      }
      miniHeader={
        <Flex align="center" justify="space-between" flex={1}>
          {getDisplayNameWO(workOrder)}
          {isNotNil(onNext) && isNotNil(onPrev) && (
            <Flex gap={4}>
              <ActionIcon color="dark" disabled={!hasPrev} onClick={onPrev} style={{ transform: 'rotate(180deg)' }}>
                <EvolveIcon icon="ArrowDown" size="xs" />
              </ActionIcon>
              <ActionIcon color="dark" disabled={!hasNext} onClick={onNext}>
                <EvolveIcon icon="ArrowDown" size="xs" />
              </ActionIcon>
            </Flex>
          )}
        </Flex>
      }
      headerRightComponent={
        <>
          {refreshing && <Loader size="sm" />}
          {hasEverBeenReleased(workOrder) && (
            <EntityStatusBadge
              customStatusEntityName="Task"
              stateName={getTaskStatusFromItem(workOrderItem)}
              size="xxl"
            />
          )}
          <Space mx="xs" />
          <EditQtyMfg item={workOrderItem} updateItem={updateWorkOrderItem} editable={editable} thin={false} />
          {editable && (
            <EditItemMenu
              item={workOrderItem}
              workOrder={workOrder}
              editable={editable}
              refreshItem={refreshWorkOrderItem}
              onMultipleItemsUpdated={onItemsAdded}
              updateItem={updateWorkOrderItem}
              onDeleteItem={onDeleteWorkOrderItem}
            />
          )}
        </>
      }
      hideModalButtons
      size="60rem"
      tall
      tabs={
        [
          {
            value: 'tasks',
            label: 'Tasks',
            hidden: !hasTasksAccess,
          },
          {
            value: 'bom',
            label: 'Bill of Materials',
            rightSection: workOrderItem.spoolHasManualEdits ? (
              <ColorSwatch color="var(--mantine-color-orange-8)" size={8} withShadow={false} />
            ) : undefined,
          },
          {
            value: 'attachments',
            label: 'Attachments',
          },
          {
            value: 'fabrication-items',
            label: 'Fabrication Items',
            hidden: !hasFabricationAccess,
          },
          {
            value: 'shipments',
            label: 'Shipments',
          },
        ] as const
      }
      selectedTab={selectedModalTab}
      onTabChange={(v) => isNotNil(v) && onModalTabChange?.(v)}
      {...props}
    >
      {(tab) => (
        <>
          {tab === 'tasks' && (
            <WorkOrderItemTaskList
              workOrderItem={workOrderItem}
              workOrder={workOrder}
              // When tasks are adjusted, we need to refresh ourselves to check if we have any tasks
              onTasksUpdated={refreshWorkOrderItem}
              onTaskAttachmentsChanged={onTaskAttachmentsChanged}
            />
          )}
          {tab === 'bom' && (
            <WorkOrderItemBOM
              workOrder={workOrder}
              workOrderItem={workOrderItem}
              // Similarly, when items are adjusted, we need to refresh ourselves to check if we have any BOM items
              onItemsChanged={refreshWorkOrderItem}
            />
          )}
          {tab === 'attachments' && (
            <AttachmentList
              workOrderItemId={workOrderItem.workOrderItemId}
              allowUploads="workOrderItem"
              // When attachments change, refresh ourselves so the item's attachment count stays in sync
              onFilesUploaded={refreshWorkOrderItem}
              onWebLinkAdded={refreshWorkOrderItem}
              onAttachmentDeleted={refreshWorkOrderItem}
            />
          )}
          {tab === 'fabrication-items' && (
            <FabricationItems
              onElementsUpdated={refreshWorkOrderItem}
              workOrderPageOpts={{
                workOrder,
                workOrderItem,
              }}
            />
          )}
          {tab === 'shipments' && <WorkOrderItemShipmentsTab workOrderItem={workOrderItem} />}
        </>
      )}
    </WrappedModal>
  );
};
